import { Entity, UniqueEntityID } from '@/domain/core'

import { toNonNegativeOrZero } from '@/utils/number'

type StationFill = {
  id: UniqueEntityID
  wbProfileAirplaneStationId: UniqueEntityID
  weight: number
}

export interface WBAirplaneFillProps {
  aircraftId: UniqueEntityID
  wbProfileAirplaneId: UniqueEntityID
  name?: string
  departure?: string
  arrival?: string
  departureTime?: Date
  pilotName?: string
  pilotCode?: string
  remark?: string
  taxiFuel?: number
  flightFuel?: number
  stationsFill?: StationFill[]
  createdAt?: Date
  updatedAt?: Date
  deletedAt?: Date
}

export interface WBAirplaneFillPojo extends WBAirplaneFillProps {
  id: string
}

export class WBAirplaneFill extends Entity<WBAirplaneFillProps, WBAirplaneFillPojo> {
  private static sanitizeOptionalNonNegative(value: number | undefined) {
    if (value === undefined || value === null) return value
    return toNonNegativeOrZero(value)
  }

  private static sanitizeProps(props: WBAirplaneFillProps): WBAirplaneFillProps {
    return {
      ...props,
      taxiFuel: WBAirplaneFill.sanitizeOptionalNonNegative(props.taxiFuel),
      flightFuel: WBAirplaneFill.sanitizeOptionalNonNegative(props.flightFuel),
      stationsFill: props.stationsFill?.map((station) => ({
        ...station,
        weight: toNonNegativeOrZero(station.weight)
      }))
    }
  }

  private constructor(props: WBAirplaneFillProps, id?: UniqueEntityID) {
    super(WBAirplaneFill.sanitizeProps(props), id)
  }

  public static create(props: WBAirplaneFillProps, id?: UniqueEntityID): WBAirplaneFill {
    return new WBAirplaneFill(props, id)
  }

  // Getters
  get aircraftId(): UniqueEntityID {
    return this.props.aircraftId
  }

  get wbProfileAirplaneId(): UniqueEntityID {
    return this.props.wbProfileAirplaneId
  }

  get name(): string | undefined {
    return this.props.name
  }

  get departure(): string | undefined {
    return this.props.departure
  }

  get arrival(): string | undefined {
    return this.props.arrival
  }

  get departureTime(): Date | undefined {
    return this.props.departureTime
  }

  get pilotName(): string | undefined {
    return this.props.pilotName
  }

  get pilotCode(): string | undefined {
    return this.props.pilotCode
  }

  get remark(): string | undefined {
    return this.props.remark
  }

  get taxiFuel(): number | undefined {
    return this.props.taxiFuel
  }

  get flightFuel(): number | undefined {
    return this.props.flightFuel
  }

  get stationsFill(): StationFill[] | undefined {
    return this.props.stationsFill
  }

  get createdAt(): Date | undefined {
    return this.props.createdAt
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt
  }

  get deletedAt(): Date | undefined {
    return this.props.deletedAt
  }

  // Setters (methods to update properties)
  public set name(name: string) {
    this.props.name = name
  }

  public set departure(departure: string) {
    this.props.departure = departure
  }

  public set arrival(arrival: string) {
    this.props.arrival = arrival
  }

  public set departureTime(departureTime: Date) {
    this.props.departureTime = departureTime
  }

  public set pilotName(pilotName: string) {
    this.props.pilotName = pilotName
  }

  public set pilotCode(pilotCode: string) {
    this.props.pilotCode = pilotCode
  }

  public set remark(remark: string) {
    this.props.remark = remark
  }

  public set taxiFuel(taxiFuel: number) {
    this.props.taxiFuel = WBAirplaneFill.sanitizeOptionalNonNegative(taxiFuel)
  }

  public set flightFuel(flightFuel: number) {
    this.props.flightFuel = WBAirplaneFill.sanitizeOptionalNonNegative(flightFuel)
  }

  public set stationsFill(stationsFill: StationFill[] | undefined) {
    this.props.stationsFill = stationsFill?.map((station) => ({
      ...station,
      weight: toNonNegativeOrZero(station.weight)
    }))
  }

  public set createdAt(createdAt: Date | undefined) {
    this.props.createdAt = createdAt
  }

  public set updatedAt(updatedAt: Date | undefined) {
    this.props.updatedAt = updatedAt
  }

  public set deletedAt(deletedAt: Date | undefined) {
    this.props.deletedAt = deletedAt
  }

  // Method to convert to POJO
  public toPOJO(): WBAirplaneFillPojo {
    return {
      id: this.id.toString(),
      aircraftId: this.props.aircraftId,
      wbProfileAirplaneId: this.props.wbProfileAirplaneId,
      name: this.props.name,
      departure: this.props.departure,
      arrival: this.props.arrival,
      departureTime: this.props.departureTime,
      pilotName: this.props.pilotName,
      pilotCode: this.props.pilotCode,
      remark: this.props.remark,
      taxiFuel: this.props.taxiFuel,
      flightFuel: this.props.flightFuel,
      stationsFill: this.props.stationsFill,
      createdAt: this.props.createdAt,
      updatedAt: this.props.updatedAt
    } as WBAirplaneFillPojo
  }

  // Static method to create from POJO
  public static fromPOJO(pojo: WBAirplaneFillPojo): WBAirplaneFill {
    return new WBAirplaneFill(
      {
        aircraftId: pojo.aircraftId,
        wbProfileAirplaneId: pojo.wbProfileAirplaneId,
        name: pojo.name,
        departure: pojo.departure,
        arrival: pojo.arrival,
        departureTime: pojo.departureTime,
        pilotName: pojo.pilotName,
        pilotCode: pojo.pilotCode,
        remark: pojo.remark,
        taxiFuel: pojo.taxiFuel,
        flightFuel: pojo.flightFuel,
        stationsFill: pojo.stationsFill
          ? pojo.stationsFill.map((station) => ({
              id: station.id,
              wbProfileAirplaneStationId: station.wbProfileAirplaneStationId,
              weight: station.weight
            }))
          : [],
        createdAt: pojo.createdAt,
        updatedAt: pojo.updatedAt
      },
      new UniqueEntityID(pojo.id)
    )
  }
}
